// @givmo/app-charity — payment audit history (CPA-WEB4).
//
// The charity-facing append-only payment history (created / changed / verified / flagged), newest first,
// from GET .../payment-events. §3.4: this is the HONEST activity trail — it carries NO bank data (only
// the event kind, an optional detail note, and a timestamp), so an org can see exactly what happened to
// its payment setup and when. Empty and error states remain explicit.
//
// The wire's `detail` is an INTERNAL token, not copy, so it is translated through `paymentEventDetail`
// and dropped when unrecognized rather than rendered verbatim.

import { useQuery } from '@tanstack/react-query';
import { charityAdminListCharityPaymentEventsOptions } from '@givmo/api-client/query';
import type { CharityPaymentEventOut } from '@givmo/api-client';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  ErrorMessage,
  Icon,
  Skeleton,
  testIdProp,
} from '@givmo/design-system';
import { History } from 'lucide-react';
import { paymentEventDetail, paymentEventLabel, paymentEventTone } from './payment-vocab';

const PREFIX = 'payment-history';

/** Format an ISO timestamp for display; tolerant of null/invalid values. */
function formatWhen(iso: string | null | undefined): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

export interface PaymentHistoryProps {
  charityId: string;
}

export function PaymentHistory({ charityId }: PaymentHistoryProps) {
  const eventsQuery = useQuery({
    ...charityAdminListCharityPaymentEventsOptions({ path: { charity_id: charityId } }),
    retry: false,
  });

  if (eventsQuery.isPending) {
    return (
      <Card testId={`${PREFIX}-loading`}>
        <CardBody className="flex flex-col gap-8">
          <Skeleton className="h-12 w-1/3" />
          <Skeleton className="h-12 w-1/2" />
        </CardBody>
      </Card>
    );
  }

  if (eventsQuery.isError) {
    return (
      <Card testId={`${PREFIX}-error`}>
        <CardBody className="flex items-center justify-between gap-12">
          <ErrorMessage>We couldn’t load payment activity.</ErrorMessage>
          <Button
            appearance="saas"
            tone="secondary"
            size="sm"
            onClick={() => void eventsQuery.refetch()}
            testId={`${PREFIX}-retry`}
          >
            Try again
          </Button>
        </CardBody>
      </Card>
    );
  }

  const events: CharityPaymentEventOut[] = eventsQuery.data?.events ?? [];
  if (events.length === 0) {
    return (
      <Card testId={`${PREFIX}-empty`}>
        <CardBody>
          <p className="text-s13 text-text-tertiary">No payment activity yet.</p>
        </CardBody>
      </Card>
    );
  }

  return (
    <Card testId={`${PREFIX}-card`}>
      <CardHeader>
        <div className="flex items-center gap-10">
          <Icon icon={History} size={18} className="text-text-secondary" aria-hidden />
          <div className="font-w600 text-text-strong">Activity</div>
        </div>
      </CardHeader>
      <CardBody>
        <ul className="flex flex-col gap-2" {...testIdProp(`${PREFIX}-list`)}>
          {events.map((event) => {
            const when = formatWhen(event.created_at);
            const detail = paymentEventDetail(event.detail);
            return (
              <li
                key={event.id}
                className="flex items-start justify-between gap-12 border-b border-border-subtle py-10 last:border-b-0"
                {...testIdProp(`${PREFIX}-row-${event.id}`)}
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-8">
                    <Badge tone={paymentEventTone(event.event_type)} size="sm">
                      {paymentEventLabel(event.event_type)}
                    </Badge>
                  </div>
                  {detail ? <p className="mt-2 text-s13 text-text-secondary">{detail}</p> : null}
                </div>
                {when ? (
                  <span className="shrink-0 text-s12 tabular-nums text-text-tertiary">{when}</span>
                ) : null}
              </li>
            );
          })}
        </ul>
      </CardBody>
    </Card>
  );
}
